import { useEffect } from 'react';
import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { PRODUCT_UPDATES } from '../data/productUpdates';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const months = [...new Set(PRODUCT_UPDATES.map((update) => update.date.slice(0, 7)))];
const formatDate = (date: string, monthOnly = false) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    ...(monthOnly ? {} : { day: 'numeric' as const }),
    year: 'numeric',
    timeZone: 'UTC',
  });

export function ProductUpdatesPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Paddiq product updates | Paddiq';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />
      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Product updates</p>
            <h1>What’s changed in Paddiq.</h1>
            <p className="guide-hero__lead">
              Improvements to session review, imports, team management, and the iPhone app. Browse
              recent changes or catch up on earlier updates.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/login')}
              >
                Open Paddiq
              </a>
              <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/resources">
                Browse the guides
              </a>
            </div>
            <p className="guide-hero__meta">
              Updated October 16, 2026 · {PRODUCT_UPDATES.length} updates
            </p>
          </div>
        </section>
        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Product update archive">
              <ResourceSidebar currentPath="/resources/product-updates" />
              <p className="guide-summary__label">Browse by month</p>
              <ol>
                {months.map((month) => (
                  <li key={month}>
                    <a href={`#updates-${month}`}>{formatDate(`${month}-01`, true)}</a>
                  </li>
                ))}
              </ol>
              <div className="guide-summary__note">
                <strong>Using the iPhone app?</strong>
                <span>
                  Available changes depend on your installed app version. Web deployment dates do
                  not establish when an iPhone feature became available.
                </span>
              </div>
            </aside>
            <article className="guide-content">
              <p>
                Dates show when the complete web update was verified in production (UTC). Parts of
                an update may have arrived earlier. Preview entries are not release announcements.
                iPhone updates without a verified App Store release date are excluded from this
                archive.
              </p>
              {months.map((month) => (
                <div id={`updates-${month}`} key={month}>
                  <p className="section-kicker">{formatDate(`${month}-01`, true)}</p>
                  {PRODUCT_UPDATES.filter((update) => update.date.startsWith(month)).map(
                    (update) => (
                      <section className="guide-step product-update" id={update.id} key={update.id}>
                        <div>
                          <p className="guide-step__eyebrow">
                            <time dateTime={update.date}>{formatDate(update.date)}</time> ·{' '}
                            {update.area}
                            {update.status === 'preview' ? ' · Preview' : ''}
                          </p>
                          <h2>{update.title}</h2>
                          {update.paragraphs.map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                          ))}
                          {update.status === 'preview' && (
                            <a className="guide-text-link" href="/paddiq-link">
                              About Paddiq Link
                            </a>
                          )}
                        </div>
                      </section>
                    ),
                  )}
                </div>
              ))}
              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Need the steps?</p>
                  <h2>Find the guide for your next session.</h2>
                  <p>Instructions for importing data, comparing laps, and managing your team.</p>
                </div>
                <div className="guide-cta__actions">
                  <a className="landing-btn landing-btn--primary landing-btn--lg" href="/resources">
                    Browse all guides
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
